import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Lead, LeadsFilters, CallLog, CallLogPayload } from '../types';

export interface LeadsResponse {
  leads: Lead[];
  total: number;
}

export function useLeads(filters: LeadsFilters) {
  return useQuery({
    queryKey: ['leads', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.niche) params.set('niche', filters.niche);
      if (filters.location) params.set('location', filters.location);
      if (filters.status) params.set('status', filters.status);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.userId) params.set('userId', filters.userId);
      params.set('limit', filters.limit.toString());
      params.set('offset', filters.offset.toString());

      const { data } = await api.get<LeadsResponse>(`/api/leads?${params.toString()}`);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useLeadDetail(id: string | null) {
  return useQuery({
    queryKey: ['leads', id],
    queryFn: async () => {
      const { data } = await api.get<{ lead: Lead }>(`/api/leads/${id}`);
      return data.lead;
    },
    enabled: !!id,
  });
}

export function useLeadCalls(id: string | null) {
  return useQuery({
    queryKey: ['leads', id, 'calls'],
    queryFn: async () => {
      const { data } = await api.get<{ calls: CallLog[] }>(`/api/leads/${id}/calls`);
      return data.calls;
    },
    enabled: !!id,
  });
}

export function useLeadMutations() {
  const queryClient = useQueryClient();

  const updateLead = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<Lead> }) => {
      const { data } = await api.patch(`/api/leads/${id}`, payload);
      return data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads', id] });
    },
  });

  const updateSubStatus = useMutation({
    mutationFn: async ({ id, sub_status }: { id: string; sub_status: string }) => {
      const { data } = await api.patch(`/api/leads/${id}/sub-status`, { sub_status });
      return data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads', id] });
    },
  });

  const updateFollowUp = useMutation({
    mutationFn: async ({ id, follow_up_date }: { id: string; follow_up_date: string | null }) => {
      const { data } = await api.patch(`/api/leads/${id}/follow-up`, { follow_up_date });
      return data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads', id] });
    },
  });

  const logCall = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: CallLogPayload }) => {
      const { data } = await api.post(`/api/leads/${id}/calls`, payload);
      return data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['leads', id, 'calls'] });
      // A call might update lead metadata (last active etc)
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  return {
    updateLead,
    updateSubStatus,
    updateFollowUp,
    logCall,
  };
}
